/**
 * Dashboard Quick Navigation
 * 
 * Provides rapid navigation between Control Domains and subdomains.
 * Uses keyboard shortcut (Cmd/Ctrl + K) for instant access.
 * 
 * @see docs/KRYSAIS_CONTROL_GLOSSARY.md - Control Domain definitions
 */

import React, { useState, useEffect, useRef } from 'react';
import { Dropdown, Input, Menu, Typography, Tag } from 'antd';
import {
  SearchOutlined,
  ControlOutlined,
  ShopOutlined,
  SettingOutlined,
  RocketOutlined,
  LineChartOutlined,
  AimOutlined,
  TeamOutlined,
  CustomerServiceOutlined,
  HistoryOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { 
  useDashboardNavigation, 
  DashboardLevel, 
  LEVEL_LABELS 
} from '../../contexts/DashboardNavigationContext';
import styles from './DashboardQuickNav.module.css';

const { Text } = Typography;

// ============================================================================
// CONTROL DOMAINS & SUBDOMAINS
// ============================================================================

interface NavItem {
  key: DashboardLevel;
  label: string;
  icon: React.ReactNode;
  description: string;
  requiresProduct?: boolean;
}

const NAVIGATION_SECTIONS: NavItem[] = [
  {
    key: 'business',
    label: 'Control Bridge',
    icon: <ControlOutlined />,
    description: 'Set intent and observe live system state',
  },
  {
    key: 'product',
    label: 'Product Control',
    icon: <ShopOutlined />,
    description: 'Product domain control and coordination',
    requiresProduct: true,
  },
  {
    key: 'operations',
    label: 'Operations',
    icon: <SettingOutlined />,
    description: 'Demand, supply, and delivery coordination',
    requiresProduct: true,
  },
  {
    key: 'development',
    label: 'Development',
    icon: <RocketOutlined />,
    description: 'Product development coordination and roadmap',
    requiresProduct: true,
  },
  {
    key: 'projections',
    label: 'Projections',
    icon: <LineChartOutlined />,
    description: 'Financial forecasts and projections',
    requiresProduct: true,
  },
  {
    key: 'marketfit',
    label: 'MarketFit',
    icon: <AimOutlined />,
    description: 'Deals, situations, and solutions',
    requiresProduct: true,
  },
  {
    key: 'stakeholders',
    label: 'Stakeholder Control',
    icon: <TeamOutlined />,
    description: 'Balance shareholder, employee, and community value',
  },
  {
    key: 'customers',
    label: 'Customer Control',
    icon: <CustomerServiceOutlined />,
    description: 'Manage customer journey and value creation',
  },
];

// ============================================================================
// COMPONENT
// ============================================================================

interface DashboardQuickNavProps {
  className?: string;
}

export const DashboardQuickNav: React.FC<DashboardQuickNavProps> = ({ className }) => {
  const { 
    currentLocation, 
    recentLocations, 
    goToLevel,
    navigateTo,
  } = useDashboardNavigation();
  
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const inputRef = useRef<any>(null);

  // Keyboard shortcut handler (Cmd/Ctrl + K)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen(prev => !prev);
      }
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Focus input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const filteredItems = NAVIGATION_SECTIONS.filter(item =>
    item.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleNavigate = (item: NavItem) => {
    if (item.requiresProduct && currentLocation.productId) {
      navigateTo({
        level: item.key,
        productId: currentLocation.productId,
        productName: currentLocation.productName,
      });
    } else if (!item.requiresProduct) {
      goToLevel(item.key);
    }
    setIsOpen(false);
    setSearchQuery('');
  };

  const dropdownContent = (
    <div className={styles.dropdownContent}>
      {/* Search Input */}
      <div className={styles.searchContainer}>
        <Input
          ref={inputRef}
          placeholder="Search dashboards..."
          prefix={<SearchOutlined />}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className={styles.searchInput}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && filteredItems.length > 0) {
              handleNavigate(filteredItems[0]);
            }
          }}
        />
        <Tag className={styles.shortcutTag}>⌘K</Tag>
      </div>

      {/* Recent Locations */}
      {recentLocations.length > 0 && !searchQuery && (
        <div className={styles.section}>
          <Text className={styles.sectionTitle}>
            <HistoryOutlined /> Recent
          </Text>
          <Menu
            className={styles.menu}
            items={recentLocations.slice(0, 5).map((loc, idx) => ({
              key: `recent-${idx}`,
              icon: NAVIGATION_SECTIONS.find(n => n.key === loc.level)?.icon,
              label: (
                <div className={styles.menuItemContent}>
                  <span>{LEVEL_LABELS[loc.level]}</span>
                  {loc.productName && (
                    <Text type="secondary" className={styles.subtitle}>
                      {loc.productName}
                    </Text>
                  )}
                </div>
              ),
              onClick: () => {
                navigateTo(loc);
                setIsOpen(false);
              },
            }))}
          />
        </div>
      )}

      {/* All Sections */}
      <div className={styles.section}>
        <Text className={styles.sectionTitle}>
          <ThunderboltOutlined /> Quick Navigate
        </Text>
        <Menu
          className={styles.menu}
          items={filteredItems.map((item) => {
            const isDisabled = item.requiresProduct && !currentLocation.productId;
            return {
              key: item.key,
              icon: item.icon,
              disabled: isDisabled,
              label: (
                <div className={styles.menuItemContent}>
                  <span>{item.label}</span>
                  <Text type="secondary" className={styles.description}>
                    {item.description}
                  </Text>
                </div>
              ),
              onClick: () => !isDisabled && handleNavigate(item),
            };
          })}
        />
      </div>
    </div>
  );

  return (
    <Dropdown
      open={isOpen}
      onOpenChange={setIsOpen}
      popupRender={() => dropdownContent}
      trigger={['click']}
      placement="bottomRight"
      className={className}
    >
      <button className={styles.triggerButton} aria-label="Quick navigation">
        <SearchOutlined />
        <span className={styles.triggerText}>Quick Nav</span>
        <Tag className={styles.shortcutTagSmall}>⌘K</Tag>
      </button>
    </Dropdown>
  );
};

export default DashboardQuickNav;
